import { useEffect, useState, useCallback } from 'react';
import { getDeviceId, detectDevice } from '../lib/installPrompt';
import { isPushSupported, getDeviceStatus, toggleTestDevice, subscribeToPush } from '../lib/pushNotifications';

export default function PwaDeviceStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState(null);
  const [label, setLabel] = useState('');

  const deviceId = getDeviceId();
  const supported = isPushSupported();
  const permission = supported ? Notification.permission : 'unsupported';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const s = await getDeviceStatus(deviceId);
      setStatus(s);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  const handleActivar = async () => {
    setWorking(true);
    setMsg(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') {
        await subscribeToPush(deviceId, detectDevice());
        await load();
      } else {
        setMsg(`No se concedió el permiso de notificaciones (estado: "${perm}"). Revisá que el navegador no lo tenga bloqueado a nivel del sistema operativo.`);
      }
    } catch (e) {
      setMsg('Error al activar: ' + (e?.message || String(e)));
    } finally {
      setWorking(false);
    }
  };

  const handleToggleTest = async () => {
    if (!status.is_test && !label.trim()) {
      setMsg('Ingresá un nombre para identificar el dispositivo.');
      return;
    }
    setWorking(true);
    setMsg(null);
    try {
      const r = await toggleTestDevice(deviceId, label.trim());
      setStatus((s) => ({ ...s, is_test: r.is_test, test_label: r.test_label }));
      setLabel('');
    } catch (e) {
      setMsg(e.message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-md w-full">
        <h1 className="text-lg font-bold text-slate-800 mb-1">Tu dispositivo</h1>
        <p className="text-sm text-slate-500 mb-5">
          Esta página identifica el dispositivo desde el que la abrís, para poder usarlo como destino de
          notificaciones de prueba desde el panel de administración.
        </p>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
          <p className="text-xs text-slate-500 mb-1">ID de este dispositivo</p>
          <p className="font-mono text-sm text-slate-800 break-all">{deviceId}</p>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400">Cargando estado…</p>
        ) : !supported ? (
          <p className="text-sm text-amber-600">Este navegador no soporta notificaciones push.</p>
        ) : !status?.subscribed ? (
          <div>
            <p className="text-sm text-slate-600 mb-3">
              Todavía no está suscripto a las notificaciones {permission === 'denied' && '(el permiso está bloqueado; habilitalo desde la configuración del sitio en el navegador)'}.
            </p>
            {permission !== 'denied' && (
              <button
                onClick={handleActivar}
                disabled={working}
                className="px-4 py-2 rounded bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 cursor-pointer"
              >
                {working ? 'Activando…' : 'Activar notificaciones'}
              </button>
            )}
          </div>
        ) : (
          <div>
            <p className="text-sm text-slate-600 mb-3">Suscripto ✓</p>
            {status.is_test ? (
              <div>
                <p className="text-sm text-violet-700 font-medium mb-3">
                  Marcado como dispositivo de prueba: "{status.test_label}"
                </p>
                <button
                  onClick={handleToggleTest}
                  disabled={working}
                  className="px-4 py-2 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-medium disabled:opacity-50 cursor-pointer"
                >
                  {working ? 'Guardando…' : 'Quitar de dispositivos de prueba'}
                </button>
              </div>
            ) : (
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Nombre para identificar este dispositivo (ej. "Mi celular", "iPhone de Ana")
                </label>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  maxLength={60}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm mb-3"
                />
                <button
                  onClick={handleToggleTest}
                  disabled={working || !label.trim()}
                  className="px-4 py-2 rounded bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 cursor-pointer"
                >
                  {working ? 'Guardando…' : 'Marcar como dispositivo de prueba'}
                </button>
              </div>
            )}
          </div>
        )}

        {msg && <p className="text-xs text-red-500 mt-3">{msg}</p>}
      </div>
    </div>
  );
}
